import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { useSiteSettings, buildWhatsAppLink } from "@/lib/SiteSettingsContext";
import { useCookieConsent } from "@/lib/cookieConsent";
import { cleanContact, hasAnyContact } from "@/lib/placeholders";

export default function HomeFooter() {
    const { settings } = useSiteSettings();
    const { openBanner } = useCookieConsent();
    const c = cleanContact(settings);
    const waHref = c.whatsapp ? buildWhatsAppLink(c.whatsapp, settings.whatsapp_default_message) : null;
    const socials = [
        { url: c.linkedin, icon: Linkedin, label: "LinkedIn" },
        { url: c.instagram, icon: Instagram, label: "Instagram" },
        { url: c.facebook, icon: Facebook, label: "Facebook" },
    ].filter((s) => s.url);
    const anyContactColumnContent = hasAnyContact(c) || waHref;

    const scrollTo = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <footer className="h-footer mt-24" data-testid="home-footer">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                <div>
                    <div className="flex items-center gap-2.5 mb-4">
                        <span className="w-9 h-9 rounded-full grid place-items-center text-white font-display font-bold text-lg" style={{ background: "var(--grad-btn)" }}>Ω</span>
                        <span className="font-display font-bold text-[15px]" style={{ color: "var(--text)" }}>{settings.company_name || "Omega IT"}</span>
                    </div>
                    <p style={{ color: "var(--muted)", fontSize: "14.5px", lineHeight: 1.65, maxWidth: "22em" }}>
                        Smart websites, apps, and digital systems — engineered to scale.
                    </p>
                    {socials.length > 0 && (
                        <div className="flex items-center gap-2 mt-5">
                            {socials.map(({ url, icon: Icon, label }) => (
                                <a key={label} href={url} target="_blank" rel="noreferrer noopener" aria-label={label} className="w-9 h-9 rounded-md border grid place-items-center" style={{ borderColor: "var(--line-soft)" }}>
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <h5>Services</h5>
                    <ul className="space-y-2.5">
                        <li><a href="#services" onClick={(e) => scrollTo(e, "services")}>Websites & Apps</a></li>
                        <li><a href="#services" onClick={(e) => scrollTo(e, "services")}>WhatsApp Integration</a></li>
                        <li><a href="#services" onClick={(e) => scrollTo(e, "services")}>UI/UX Design</a></li>
                        <li><a href="#services" onClick={(e) => scrollTo(e, "services")}>SEO & Digital Marketing</a></li>
                    </ul>
                </div>
                <div>
                    <h5>Company</h5>
                    <ul className="space-y-2.5">
                        <li><a href="#work" onClick={(e) => scrollTo(e, "work")}>Our work</a></li>
                        <li><a href="#process" onClick={(e) => scrollTo(e, "process")}>Process</a></li>
                        <li><a href="#why" onClick={(e) => scrollTo(e, "why")}>Why us</a></li>
                        <li><Link to="/about">About</Link></li>
                        <li><Link to="/pricing">Pricing</Link></li>
                        <li><Link to="/blog">Blog</Link></li>
                    </ul>
                </div>
                <div data-testid="home-footer-contact-column">
                    <h5>Get in touch</h5>
                    {anyContactColumnContent ? (
                        <ul className="space-y-3">
                            {c.email && (
                                <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-1" style={{ color: "var(--muted-2)" }} /><a href={`mailto:${c.email}`}>{c.email}</a></li>
                            )}
                            {c.phone && (
                                <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-1" style={{ color: "var(--muted-2)" }} /><a href={`tel:${c.phone.replace(/\s/g, "")}`}>{c.phone}</a></li>
                            )}
                            {waHref && (
                                <li className="flex items-start gap-2"><MessageCircle className="w-4 h-4 mt-1" style={{ color: "var(--teal)" }} /><a href={waHref} target="_blank" rel="noreferrer noopener">Chat on WhatsApp</a></li>
                            )}
                        </ul>
                    ) : (
                        <Link to="/contact" data-testid="home-footer-contact-fallback-link" className="inline-flex items-center gap-1.5">
                            Use the contact form <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
            </div>
            <div style={{ borderTop: "1px solid var(--line-soft)" }}>
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs" style={{ color: "var(--muted-2)" }}>
                    <div>© {new Date().getFullYear()} {settings.company_name || "Omega IT Ltd"}. All rights reserved.</div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={openBanner}
                            data-testid="footer-cookie-settings"
                            style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "var(--muted-2)", font: "inherit" }}
                        >
                            Cookie settings
                        </button>
                        <span>Built to scale.</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
